import React, { useState, useEffect } from "react";
import { PouchPayModal } from "./Modal";
import { PouchPayClient, type CryptoChannel } from "@pouchpay/sdk";

// Use production API by default for testing real-world integration
const apiBaseUrl =
  import.meta.env.VITE_API_BASE_URL || "https://api.pouchfinance.xyz";
console.log("🌐 API Base URL:", apiBaseUrl);

export default function App() {
  const [showWidget, setShowWidget] = useState(false);
  const [config, setConfig] = useState<any>(null);

  // Form state
  const [apiKey, setApiKey] = useState("");
  const [sessionType, setSessionType] = useState<"ONRAMP" | "OFFRAMP">(
    "ONRAMP",
  );
  const [cryptoCurrency, setCryptoCurrency] = useState("");
  const [cryptoNetwork, setCryptoNetwork] = useState("");
  const [walletAddress, setWalletAddress] = useState("");
  const [amountUsd, setAmountUsd] = useState("10");
  const [cryptoAmount, setCryptoAmount] = useState("25");

  // Crypto channels from API
  const [cryptoChannels, setCryptoChannels] = useState<CryptoChannel[]>([]);
  const [channelsLoading, setChannelsLoading] = useState(true);
  const [channelsError, setChannelsError] = useState<string | null>(null);

  // Available networks for selected currency
  const [availableNetworks, setAvailableNetworks] = useState<
    Array<{ network: string; name: string }>
  >([]);

  // Load API key and crypto channels on mount
  useEffect(() => {
    const savedApiKey = localStorage.getItem("pouchpay_dev_api_key");
    if (savedApiKey) {
      setApiKey(savedApiKey);
    }

    // Fetch crypto channels from API
    let isMounted = true;

    const loadChannels = async () => {
      try {
        setChannelsLoading(true);
        setChannelsError(null);
        const client = new PouchPayClient({ baseUrl: apiBaseUrl });
        const response = await client.getCryptoChannels();

        // Only update state if component is still mounted
        if (!isMounted) return;

        // Filter to enabled channels only
        // const enabled = response.channels.filter(c => c.enabled);//removed the filter to show all channels including disabled ones for testing purposes, also i asked yc
        const enabled = response.channels;
        setCryptoChannels(enabled);

        // Set default currency to first enabled channel
        if (enabled.length > 0) {
          const defaultChannel = enabled[0];
          setCryptoCurrency(defaultChannel.code);

          // Set default network for this currency
          if (defaultChannel.defaultNetwork) {
            setCryptoNetwork(defaultChannel.defaultNetwork);
            setDefaultWalletAddress(defaultChannel.defaultNetwork);
          }
        }
      } catch (error: any) {
        // Ignore abort errors from React strict mode
        if (error.name === "AbortError") {
          console.log("Fetch aborted (likely due to React strict mode)");
          return;
        }

        console.error("Failed to fetch crypto channels:", error);
        if (isMounted) {
          setChannelsError(error.message || "Failed to load currencies");
        }
      } finally {
        if (isMounted) {
          setChannelsLoading(false);
        }
      }
    };

    loadChannels();

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, []);

  // Update available networks when currency changes
  useEffect(() => {
    if (!cryptoCurrency || cryptoChannels.length === 0) return;

    const selectedChannel = cryptoChannels.find(
      (c) => c.code === cryptoCurrency,
    );
    if (selectedChannel) {
      // Get all networks for this currency (ignoring enabled flag for testing)
      const networks = Object.entries(selectedChannel.networks)
        // .filter(([_, net]: [string, any]) => net.enabled) // Commented out to show all networks
        .map(([key, net]: [string, any]) => ({
          network: key,
          name: net.name,
        }));

      setAvailableNetworks(networks);

      // Set default network if not already set or invalid
      if (
        !cryptoNetwork ||
        !networks.find((n) => n.network === cryptoNetwork)
      ) {
        const defaultNet =
          selectedChannel.defaultNetwork || networks[0]?.network;
        if (defaultNet) {
          setCryptoNetwork(defaultNet);
          setDefaultWalletAddress(defaultNet);
        }
      }
    }
  }, [cryptoCurrency, cryptoChannels]);

  // Update default wallet when network changes
  useEffect(() => {
    if (cryptoNetwork) {
      setDefaultWalletAddress(cryptoNetwork);
    }
  }, [cryptoNetwork]);

  const setDefaultWalletAddress = (network: string) => {
    // Set example wallet addresses based on network
    if (network === "TRC20") {
      setWalletAddress("TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t");
    } else if (network === "ERC20" || network === "BASE") {
      setWalletAddress("0xde0B295669a9FD93d5F28D9Ec85E40f4cb697BAe");
    } else if (network === "XLM") {
      setWalletAddress(
        "GCZJM35NKGVK47BB4SPBDV25477PZYIYPVVG453LPYFNXLS3FGHDXOCM",
      );
    } else if (network === "SOL") {
      setWalletAddress("7v91N7iZ9mNicL8WfG6cgSCKyRXydQjLh6UYBWwm6y1Q");
    } else if (network === "BTC") {
      setWalletAddress("bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh");
    } else if (network === "TON") {
      setWalletAddress("EQCvxJy4eG8hyHBFsZ7eePxrRsUQSFE_jpptRAYBmcG_DOGS");
    } else if (
      network === "CELO" ||
      network === "POLYGON" ||
      network.includes("BSC") ||
      network.includes("BEP")
    ) {
      setWalletAddress("0xde0B295669a9FD93d5F28D9Ec85E40f4cb697BAe");
    } else {
      // Default to EVM address for unknown networks
      setWalletAddress("0xde0B295669a9FD93d5F28D9Ec85E40f4cb697BAe");
    }
  };

  const handleLaunchWidget = (e: React.FormEvent) => {
    e.preventDefault();

    if (!apiKey) {
      alert("❌ Please enter your API key");
      return;
    }

    if (!cryptoCurrency) {
      alert("❌ Please select a crypto currency");
      return;
    }

    if (!cryptoNetwork) {
      alert("❌ Please select a network");
      return;
    }

    // Wallet address is only required for ONRAMP (buying crypto)
    if (sessionType === "ONRAMP" && !walletAddress) {
      alert("❌ Please enter a wallet address");
      return;
    }

    // Save API key to localStorage
    localStorage.setItem("pouchpay_dev_api_key", apiKey);

    // Build the createSession payload
    const createSessionPayload: any = {
      type: sessionType,
      countryCode: "NG",
      currency: "NGN",
      cryptoCurrency: cryptoCurrency,
      cryptoNetwork: cryptoNetwork,
    };

    // Only include wallet address for ONRAMP (buying crypto)
    if (sessionType === "ONRAMP" && walletAddress) {
      createSessionPayload.walletAddress = walletAddress;
    }

    // Add amount based on type
    if (sessionType === "ONRAMP" && amountUsd) {
      createSessionPayload.amount = Number(amountUsd);
    } else if (sessionType === "OFFRAMP" && cryptoAmount) {
      createSessionPayload.cryptoAmount = Number(cryptoAmount);
    }

    console.log("🚀 Launching PouchPay Widget...");
    console.log("API Base URL:", apiBaseUrl);
    console.log("API Key:", apiKey);
    console.log("Create Session Payload:", createSessionPayload);

    setConfig({
      apiKey,
      createSession: createSessionPayload,
    });
    setShowWidget(true);
  };

  const handleBackToConfig = () => {
    setShowWidget(false);
    setConfig(null);
  };

  if (showWidget && config) {
    return (
      <div className="pp-dev-shell">
        <header className="pp-dev-header">
          <div>
            <h1>PouchPay Widget</h1>
            <p>Development harness for the embeddable widget.</p>
          </div>
          <button
            onClick={handleBackToConfig}
            style={{
              padding: "8px 16px",
              background: "#6c757d",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            ← Back to Config
          </button>
        </header>
        <main className="pp-dev-main">
          <PouchPayModal
            isOpen
            onClose={() => setShowWidget(false)}
            apiBaseUrl={apiBaseUrl}
            apiKey={config.apiKey}
            createSession={config.createSession}
          />
        </main>
      </div>
    );
  }

  return (
    <div className="pp-dev-shell">
      <header className="pp-dev-header">
        <div>
          <h1>🚀 PouchPay Widget Configuration</h1>
          <p>Configure and test the widget with custom settings</p>
        </div>
      </header>
      <main
        className="pp-dev-main"
        style={{ maxWidth: "800px", margin: "0 auto" }}
      >
        <div
          style={{
            background: "white",
            padding: "24px",
            borderRadius: "8px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <div
            style={{
              background:
                apiBaseUrl.includes("localhost") ||
                apiBaseUrl.includes("127.0.0.1")
                  ? "#fff3e0"
                  : "#e8f5e9",
              color:
                apiBaseUrl.includes("localhost") ||
                apiBaseUrl.includes("127.0.0.1")
                  ? "#e65100"
                  : "#2e7d32",
              border:
                apiBaseUrl.includes("localhost") ||
                apiBaseUrl.includes("127.0.0.1")
                  ? "1px solid #ffb74d"
                  : "1px solid #81c784",
              padding: "12px",
              borderRadius: "4px",
              marginBottom: "20px",
            }}
          >
            <strong>
              {apiBaseUrl.includes("localhost") ||
              apiBaseUrl.includes("127.0.0.1")
                ? "⚠️ Local Development API:"
                : "🌐 Production API:"}
            </strong>{" "}
            <code
              style={{
                background: "#f5f5f5",
                padding: "2px 6px",
                borderRadius: "3px",
                fontFamily: "monospace",
                fontSize: "13px",
              }}
            >
              {apiBaseUrl}
            </code>
            <br />
            <small
              style={{ fontSize: "12px", marginTop: "8px", display: "block" }}
            >
              {apiBaseUrl.includes("localhost") ||
              apiBaseUrl.includes("127.0.0.1")
                ? "Using local API. Set VITE_API_BASE_URL to test production."
                : "Testing with real production API. Make sure you have a valid API key from dashboard.pouchfinance.xyz"}
            </small>
          </div>

          <form onSubmit={handleLaunchWidget}>
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "500",
                  color: "#333",
                }}
              >
                API Key *
              </label>
              <input
                type="text"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="pk_test_..."
                required
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  fontSize: "14px",
                  boxSizing: "border-box",
                }}
              />
              <small
                style={{
                  display: "block",
                  color: "#666",
                  marginTop: "5px",
                  fontSize: "12px",
                }}
              >
                Get your API key from the dashboard
              </small>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "500",
                  color: "#333",
                }}
              >
                Session Type *
              </label>
              <select
                value={sessionType}
                onChange={(e) =>
                  setSessionType(e.target.value as "ONRAMP" | "OFFRAMP")
                }
                required
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  fontSize: "14px",
                  boxSizing: "border-box",
                }}
              >
                <option value="ONRAMP">Onramp (Buy Crypto)</option>
                <option value="OFFRAMP">Offramp (Sell Crypto)</option>
              </select>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "15px",
                marginBottom: "20px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "500",
                    color: "#333",
                  }}
                >
                  Crypto Currency *
                </label>
                <select
                  value={cryptoCurrency}
                  onChange={(e) => setCryptoCurrency(e.target.value)}
                  required
                  disabled={channelsLoading}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    fontSize: "14px",
                    boxSizing: "border-box",
                    opacity: channelsLoading ? 0.6 : 1,
                    cursor: channelsLoading ? "not-allowed" : "pointer",
                  }}
                >
                  {channelsLoading ? (
                    <option value="">Loading currencies...</option>
                  ) : channelsError ? (
                    <option value="">Error loading currencies</option>
                  ) : cryptoChannels.length === 0 ? (
                    <option value="">No currencies available</option>
                  ) : (
                    cryptoChannels.map((channel) => (
                      <option key={channel.code} value={channel.code}>
                        {channel.name} ({channel.code})
                      </option>
                    ))
                  )}
                </select>
                {channelsError && (
                  <small
                    style={{
                      display: "block",
                      color: "#d32f2f",
                      marginTop: "5px",
                      fontSize: "12px",
                    }}
                  >
                    {channelsError}
                  </small>
                )}
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "500",
                    color: "#333",
                  }}
                >
                  Network *
                </label>
                <select
                  value={cryptoNetwork}
                  onChange={(e) => setCryptoNetwork(e.target.value)}
                  required
                  disabled={channelsLoading || availableNetworks.length === 0}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    fontSize: "14px",
                    boxSizing: "border-box",
                    opacity:
                      channelsLoading || availableNetworks.length === 0
                        ? 0.6
                        : 1,
                    cursor:
                      channelsLoading || availableNetworks.length === 0
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  {channelsLoading ? (
                    <option value="">Loading networks...</option>
                  ) : availableNetworks.length === 0 ? (
                    <option value="">No networks available</option>
                  ) : (
                    availableNetworks.map((net) => (
                      <option key={net.network} value={net.network}>
                        {net.name}
                      </option>
                    ))
                  )}
                </select>
              </div>
            </div>

            {/* Wallet address only required for ONRAMP (buying crypto) */}
            {sessionType === "ONRAMP" && (
              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "500",
                    color: "#333",
                  }}
                >
                  Wallet Address *
                </label>
                <input
                  type="text"
                  value={walletAddress}
                  onChange={(e) => setWalletAddress(e.target.value)}
                  placeholder="TLPx3uGPY86fLJsHMPwXqGZ9RLyDsVLzVq or 0x742d35..."
                  required
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    fontSize: "14px",
                    fontFamily: "monospace",
                    boxSizing: "border-box",
                  }}
                />
                <small
                  style={{
                    display: "block",
                    color: "#666",
                    marginTop: "5px",
                    fontSize: "12px",
                  }}
                >
                  TRC20: TRON address (T...), ERC20: EVM address (0x...)
                </small>
              </div>
            )}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "15px",
                marginBottom: "20px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "500",
                    color: "#333",
                  }}
                >
                  Amount (USD) - for Onramp
                </label>
                <input
                  type="number"
                  value={amountUsd}
                  onChange={(e) => setAmountUsd(e.target.value)}
                  placeholder="10"
                  min="1"
                  step="0.01"
                  disabled={sessionType === "OFFRAMP"}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    fontSize: "14px",
                    boxSizing: "border-box",
                    opacity: sessionType === "OFFRAMP" ? 0.5 : 1,
                  }}
                />
                <small
                  style={{
                    display: "block",
                    color: "#666",
                    marginTop: "5px",
                    fontSize: "12px",
                  }}
                >
                  Yellow Card will calculate NGN equivalent
                </small>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "500",
                    color: "#333",
                  }}
                >
                  Crypto Amount - for Offramp
                </label>
                <input
                  type="number"
                  value={cryptoAmount}
                  onChange={(e) => setCryptoAmount(e.target.value)}
                  placeholder="25"
                  step="0.01"
                  disabled={sessionType === "ONRAMP"}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    fontSize: "14px",
                    boxSizing: "border-box",
                    opacity: sessionType === "ONRAMP" ? 0.5 : 1,
                  }}
                />
              </div>
            </div>

            <div style={{ marginTop: "24px" }}>
              <button
                type="submit"
                style={{
                  background: "#007bff",
                  color: "white",
                  border: "none",
                  padding: "12px 24px",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: "500",
                  width: "100%",
                }}
              >
                🚀 Launch Widget
              </button>
            </div>
          </form>

          <div
            style={{
              background: "#e8f5e9",
              color: "#2e7d32",
              border: "1px solid #81c784",
              padding: "12px",
              borderRadius: "4px",
              marginTop: "20px",
              fontSize: "14px",
            }}
          >
            <strong>💡 Tip:</strong> Your API key is saved to localStorage and
            will persist across page reloads.
          </div>
        </div>
      </main>
    </div>
  );
}
